<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: What flowed into and out of each account in the window?
  Answer:   each account's inflow, outflow and net, in the chart of accounts' own order.
  Context:  transfers in and out, and how many entries touched the account.
  Controls: the window, bucket and tab strip in the frame.
-->
<script setup lang="ts">
import {
  AccountFlowTable,
  canViewReporting,
  ReportFrame,
  useAccountFlowSummariesQuery,
  useReportNames,
  useReportScope,
  type AccountFlowRow,
} from '@/features/reporting'
import { canViewAccountBalances } from '@/features/accounts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { EmptyState } from '@taler/ui'

const { t } = useI18n()
const scope = useReportScope(
  () => canViewReporting(scope.session.can) && canViewAccountBalances(scope.session.can),
)
const { session, state } = scope
const flows = useAccountFlowSummariesQuery(scope.flows())
const names = useReportNames()

const rows = computed<AccountFlowRow[]>(() => {
  const summaries = new Map((flows.data.value?.accounts ?? []).map((account) => [account.accountId, account]))
  return names.accounts.value.flatMap((account) => {
    const figures = summaries.get(account.id)
    if (figures === undefined) return []
    return [
      {
        key: account.id,
        label: account.label,
        icon: account.icon,
        count: figures.count,
        inflow: figures.inflow,
        outflow: figures.outflow,
        transfersIn: figures.transfersIn,
        transfersOut: figures.transfersOut,
        net: figures.net,
      },
    ]
  })
})
</script>

<template>
  <ReportFrame :title="t('reporting.title')" :subtitle="t('reporting.overview.subtitle')" :state="state">
    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <EmptyState v-else-if="!canViewAccountBalances(session.can)" :title="t('reporting.accounts.noAccess')" />
    <p v-else-if="!canViewReporting(session.can)" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noAccess') }}
    </p>
    <AccountFlowTable v-else-if="flows.data.value" :rows="rows" :caption="t('reporting.tabs.accounts')" />
  </ReportFrame>
</template>
